import React, { useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import Swal from "sweetalert2";
import { FaTrash, FaArrowLeft, FaArrowRight } from "react-icons/fa";

const PunchingPage = ({ fetchData, onDelete }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentPage, setCurrentPage] = useState(new Date().getMonth());

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetchData();
        if (response.success) {
          setData(response.data);
        } else {
          // Swal.fire({
          //     title: "Error",
          //     text: `Failed to fetch data: ${response.error}`,
          //     icon: "error",
          //     confirmButtonText: "Retry",
          //     confirmButtonColor: "#FFA500",
          // });
        }
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: `An error occurred: ${error.message}`,
          icon: "error",
          confirmButtonText: "Retry",
          confirmButtonColor: "#FFA500",
        });
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [fetchData]);

  const handleDelete = async (recordId) => {
    const confirmDelete = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (confirmDelete.isConfirmed) {
      try {
        const response = await onDelete(recordId);
        if (response.success) {
          Swal.fire({
            title: "Deleted!",
            text: "Record has been deleted.",
            icon: "success",
            confirmButtonText: "OK",
            confirmButtonColor: "#FFA500",
          });
          setData(data.filter((record) => record.id !== recordId));
        } else {
          Swal.fire({
            title: "Error",
            text: `Failed to delete: ${response.error}`,
            icon: "error",
            confirmButtonText: "Retry",
            confirmButtonColor: "#FFA500",
          });
        }
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: `An error occurred: ${error.message}`,
          icon: "error",
          confirmButtonText: "Retry",
          confirmButtonColor: "#FFA500",
        });
      }
    }
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return "None";
    // return new Date(isoDate).toLocaleDateString("en-US", { timeZone: "Asia/Taipei" });
    // return new Date(isoDate).toLocaleDateString("zh-TW", { timeZone: "Asia/Taipei" });

    const date = new Date(isoDate);
    const dateString = date.toLocaleDateString("zh-TW", {
      timeZone: "Asia/Taipei",
    });
    const weekday = date.toLocaleDateString("zh-TW", {
      weekday: "short",
      timeZone: "Asia/Taipei",
    }); // weekday: "long"

    return `${dateString} (${weekday.replace("週", "")})`;
  };

  const formatTime = (isoDate) => {
    if (!isoDate) return "None";
    return new Date(isoDate).toLocaleTimeString("zh-TW", {
      timeZone: "Asia/Taipei",
    });
  };

  const calculateWorkHours = (record) => {
    // Check if clock_in exists
    if (!record.clock_in) {
      return "--"; // No clock_in, return placeholder
    }
    const clockIn = new Date(record.clock_in);

    // Check if clock_out exists
    if (!record.clock_out) {
      return "--"; // No clock_out, return placeholder
    }
    const clockOut = new Date(record.clock_out);
    let breakDuration = 0;

    // Calculate break duration only if both start_break and end_break exist
    if (record.start_break && record.end_break) {
      const startBreak = new Date(record.start_break);
      const endBreak = new Date(record.end_break);
      breakDuration = endBreak - startBreak;
    }

    // Calculate total work time
    const totalWorkTime =
      (clockOut - clockIn - breakDuration) / (1000 * 60 * 60);
    return totalWorkTime.toFixed(2);
  };

  const calculateTotalWorkHours = () => {
    return filteredData
      .reduce((total, record) => {
        const hours = parseFloat(calculateWorkHours(record));
        return !isNaN(hours) ? total + hours : total;
      }, 0)
      .toFixed(2);
  };

  const getWorkContent = (record) => {
    const content = record.event || record.work_content || record.workContent || "";
    return content.trim() || "未分類";
  };

  const getRecordTime = (record) => {
    const timestamp = new Date(
      record.clock_in || record.date || record.created_at || record.updated_at || "",
    ).getTime();
    return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER;
  };

  const filteredData = useMemo(() => {
    return data
      .map((record, index) => ({ record, index }))
      .filter(({ record }) => {
        const recordDate = new Date(record.clock_in);
        return (
          recordDate.getMonth() === currentPage &&
          recordDate.getFullYear() === currentYear
        );
      })
      .sort((a, b) => {
        const timeDifference = getRecordTime(a.record) - getRecordTime(b.record);
        return timeDifference || a.index - b.index;
      })
      .map(({ record }) => record);
  }, [currentPage, currentYear, data]);

  const handlePrevMonth = () => {
    // setCurrentPage((prev) => (prev > 0 ? prev - 1 : 11));
    setCurrentPage((prev) => {
      if (prev === 0) {
        setCurrentYear((y) => y - 1); // 跨年
        return 11;
      }
      return prev - 1;
    });
  };

  const handleNextMonth = () => {
    // setCurrentPage((prev) => (prev < 11 ? prev + 1 : 0));
    setCurrentPage((prev) => {
      if (prev === 11) {
        setCurrentYear((y) => y + 1); // 跨年
        return 0;
      }
      return prev + 1;
    });
  };

  if (loading) {
    return (
      <LoadingState role="status" aria-live="polite">
        <LoadingCard>
          <LoadingDots aria-hidden="true">
            <span />
            <span />
            <span />
          </LoadingDots>
          <LoadingTitle>Loading records</LoadingTitle>
          <LoadingText>Preparing your time clock data...</LoadingText>
        </LoadingCard>
      </LoadingState>
    );
  }

  return (
    <PageContainer>
      <h3 className="text-center">Time Clock Records</h3>
      <h6
        className="text-center"
        style={{ marginBottom: "12px", color: "var(--app-muted-text, gray)" }}
      >
        -- Total working hours: {calculateTotalWorkHours()} h --
      </h6>
      <PaginationControls>
        <PaginationButton onClick={handlePrevMonth}>
          <FaArrowLeft />
        </PaginationButton>
        <span>
          {/* new Date(0, currentPage).toLocaleString("zh-TW", { month: "long" }) */}
          {currentYear} 年{" "}
          {new Date(0, currentPage).toLocaleString("zh-TW", { month: "long" })}
        </span>
        <PaginationButton onClick={handleNextMonth}>
          <FaArrowRight />
        </PaginationButton>
      </PaginationControls>
      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Event</th>
              <th>Clock In</th>
              <th>Start Break</th>
              <th>End Break</th>
              <th>Clock Out</th>
              <th>Work Hours</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length > 0 ? (
              filteredData.map((record) => (
                <tr key={record.id}>
                  <td>{formatDate(record.clock_in)}</td>
                  <td>
                    <WorkContentBadge>{getWorkContent(record)}</WorkContentBadge>
                  </td>
                  <td>{formatTime(record.clock_in)}</td>
                  <td>{formatTime(record.start_break)}</td>
                  <td>{formatTime(record.end_break)}</td>
                  <td>{formatTime(record.clock_out)}</td>
                  <td>{calculateWorkHours(record)}</td>
                  <td>
                    <DeleteButton onClick={() => handleDelete(record.id)}>
                      <FaTrash />
                    </DeleteButton>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8">No records found.</td>
              </tr>
            )}
          </tbody>
        </Table>
      </TableContainer>
    </PageContainer>
  );
};

export default PunchingPage;

// Styling
const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  color: var(--app-text, #202124);
  font-family: Arial, sans-serif;
`;

const PaginationControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
  font-size: 1.2rem;

  span {
    margin: 0 15px;
  }
`;

const PaginationButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  font-size: 1.5rem;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: #007bff;
  }

  &:focus {
    outline: none;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 1120px;
  border-collapse: collapse;
  color: var(--app-surface-text, #202124);
  background: var(--app-surface, #fff);

  th,
  td {
    border: 1px solid var(--app-border, #ddd);
    padding: 12px;
    text-align: center;
    font-size: 0.9rem;
    vertical-align: middle;
  }

  th:nth-child(2),
  td:nth-child(2) {
    min-width: 150px;
  }

  th {
    background-color: var(--app-surface-soft, #f7f7f7);
    font-weight: bold;
  }

  tr:nth-child(even) {
    background-color: var(--app-surface-hover, #f9f9f9);
  }

  tr:hover {
    background-color: var(--app-surface-soft, #f1f1f1);
  }

  @media (max-width: 768px) {
    th,
    td {
      font-size: 0.8rem;
      padding: 8px;
    }
  }
`;

const WorkContentBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 180px;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(48, 133, 214, 0.12);
  color: #1769aa;
  font-size: 0.86rem;
  font-weight: 800;
  line-height: 1.25;
  overflow-wrap: anywhere;
`;

const dotBounce = keyframes`
  0%, 80%, 100% {
    transform: translateY(0);
    opacity: 0.45;
  }

  40% {
    transform: translateY(-10px);
    opacity: 1;
  }
`;

const LoadingState = styled.div`
  min-height: 360px;
  display: grid;
  place-items: center;
  padding: 32px 16px;
  color: var(--app-text, #202124);
`;

const LoadingCard = styled.div`
  width: min(360px, 100%);
  display: grid;
  justify-items: center;
  gap: 10px;
  padding: 28px 24px;
  border: 1px solid var(--app-border, #dbe5ea);
  border-radius: 8px;
  background: var(--app-surface, #ffffff);
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.1);
`;

const LoadingDots = styled.div`
  height: 24px;
  display: inline-flex;
  align-items: end;
  gap: 8px;
  margin-bottom: 2px;

  span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #1769aa;
    animation: ${dotBounce} 1s infinite ease-in-out;
  }

  span:nth-child(2) {
    animation-delay: 0.14s;
  }

  span:nth-child(3) {
    animation-delay: 0.28s;
  }
`;

const LoadingTitle = styled.div`
  font-size: 1.08rem;
  font-weight: 800;
`;

const LoadingText = styled.div`
  text-align: center;
  font-size: 0.9rem;
  color: var(--app-muted-text, #667085);

  @media (max-width: 768px) {
    font-size: 0.86rem;
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: #d33;
  font-size: 1.2rem;
  cursor: pointer;
  transition:
    color 0.3s ease,
    transform 0.2s ease;

  &:hover {
    color: #a00;
    transform: scale(1.1);
  }

  &:focus {
    outline: none;
  }
`;
